// Keeps the URL hash in sync with which `<details>` section is open, so an
// expanded section can be deep-linked / shared. Wired into the `:::details`
// renderer in Markdown.tsx via the native `<details onToggle>` event.
//
// Behavior (mirrors how the existing deep-link reader in
// `useAnchorDeepLink.ts` expects `#<slug>` to point at a details section):
//   - Open a section            → set the hash to that section's slug.
//   - Close the section that
//     currently owns the hash    → clear the hash.
//   - Open a new section while
//     another already owns the
//     hash                       → the hash moves to the newly-opened section.
//
// We use `history.replaceState` rather than assigning `location.hash` so that
// toggling sections doesn't spam the browser's back/forward history with an
// entry per click.

// Write the hash without pushing a new history entry. Passing an empty string
// removes the hash entirely (and the trailing `#`), leaving the path+query.
export function setHashWithoutHistory(slug: string): void {
  const { pathname, search } = window.location;
  const url = slug ? `${pathname}${search}#${slug}` : `${pathname}${search}`;
  window.history.replaceState(window.history.state, '', url);
}

// Read the current hash slug (without the leading `#`), URL-decoded.
function currentHashSlug(): string {
  const raw = window.location.hash.replace(/^#/, '');
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

// Handle one `<details>` toggle. `isOpen` is the details' new state.
export function syncDetailsHash(slug: string, isOpen: boolean): void {
  if (typeof window === 'undefined') return;
  if (isOpen) {
    // Opening always claims the hash — even if another section is still open,
    // the most-recently-opened one wins.
    setHashWithoutHistory(slug);
  } else if (currentHashSlug() === slug) {
    // Only clear the hash if the section being closed is the one that owns it;
    // closing a non-owning section must not disturb another's deep link.
    setHashWithoutHistory('');
  }
}

// Deep-link half of the sync: a page loaded at `#<slug>` should show that
// collapsible section expanded and scrolled into view. Wired into a
// collapsible's renderer as a ref/effect callback, so it runs when the element
// mounts. The playbook has richer machinery for this (useAnchorDeepLink expands
// the right branch and runs a stabilizing scroll loop); opening here is
// idempotent and that loop re-aims any scroll we start, so the two cooperate.
// On pages without it (supplements, perspective pages) this is the only handler.
//
// The load-time hash is captured once and handled at most once, so later
// remounts (the playbook remounts markdown on plan switches) can't yank the
// reader back to a section they merely toggled earlier in the visit.
let initialHashSlug: string | null = null;
let didHandleInitialHash = false;

// Generic core, shared by every collapsible that participates in hash sync
// (`:::details`, the incrementalist wishlist, …). If this element's `slug` is
// the slug the page was loaded at, run `expand()` to reveal it, then scroll it
// into view once its content has laid out. `expand` owns whatever "open me"
// means for the caller (toggle `.open` on a `<details>`, set React state, …).
export function openCollapsibleIfHashTarget(
  el: HTMLElement | null,
  slug: string,
  expand: () => void,
): void {
  if (typeof window === 'undefined' || !el || !slug) return;
  if (initialHashSlug === null) initialHashSlug = currentHashSlug();
  if (didHandleInitialHash || slug !== initialHashSlug) return;
  didHandleInitialHash = true;
  expand();
  // Wait a frame so the just-opened content is laid out before measuring.
  requestAnimationFrame(() => el.scrollIntoView());
}

// `:::details` deep-link: expand the target and every enclosing `<details>`
// (outermost inward, target last, so the target's onToggle → syncDetailsHash is
// the one that claims the hash — the last to fire wins).
export function openDetailsIfHashTarget(el: HTMLDetailsElement | null, slug: string): void {
  openCollapsibleIfHashTarget(el, slug, () => {
    const chain: HTMLDetailsElement[] = [];
    let current: HTMLElement | null = el;
    while (current) {
      const enclosing: HTMLDetailsElement | null = current.closest('details');
      if (!enclosing) break;
      chain.push(enclosing);
      current = enclosing.parentElement;
    }
    for (const details of chain.reverse()) details.open = true;
  });
}

// ─── Footnote round-trip ────────────────────────────────────────────────────
//
// A footnote number opens its definition in a modal (FootnoteModal), whose
// permalink links out to the standalone `/footnotes` page (modified clicks —
// new tab/window — on the number itself navigate there directly too). The
// `↩` back-arrow on that page must return the reader to *where they were*, not a
// hardcoded `/`: the playbook encodes the open branch in `?choices=`, and the
// reference itself lives at `#footnote-<id>`. So we carry the originating page's
// path+query into `/footnotes` as a `from` param, and rebuild it on the way
// back. Landing on `<from>#footnote-<id>` re-runs the same hash→scroll machinery
// `:::details` uses (useAnchorDeepLink on the playbook, native anchor jump
// elsewhere), so the reader lands back on the exact superscript they left from.

const FOOTNOTES_PATH = '/footnotes';

// Build the outbound footnote link, tagging it with the current page so the
// return trip can come back here. Falls back to a bare `/footnotes#<anchor>`
// during SSR (no `window`); the click-time rewrite in FootnoteRef supplies the
// real `from` on the client.
export function footnotesLinkWithOrigin(anchor: string): string {
  const from =
    typeof window === 'undefined'
      ? ''
      : `${window.location.pathname}${window.location.search}`;
  const query = from ? `?from=${encodeURIComponent(from)}` : '';
  return `${FOOTNOTES_PATH}${query}#${anchor}`;
}

// The `/footnotes` back-arrow target: the origin page (from the `from` param)
// plus the `#footnote-<id>` fragment. Defaults to the site root when no origin
// was carried (e.g. someone opened /footnotes directly).
export function footnoteReturnHref(from: string | null | undefined, identifier: string): string {
  const base = from && from.startsWith('/') ? from : '/';
  return `${base}#footnote-${identifier}`;
}
